import React, { useEffect, useMemo } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/string/pluralize'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { connect } from 'react-redux'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import { getBundledWithFlightOfferDisplayPricing } from 'lib/offer/getOfferDisplayPricing'
import config from 'constants/config'
import * as Analytics from 'analytics/analytics'
import { logOperational } from 'analytics/snowplow/events'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'

interface Props {
  duration?: number;
  offer: App.Offer | App.OfferSummary;
  airportCode: string;
}

function BundledHotelPriceContent({
  duration,
  airportCode,
  offer,
}: Props) {
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      Analytics.track('log-operational', logOperational('BundledHotelPriceContent', `duration_not_finite::${duration}`))
    }
  }, [duration])

  if (!lowestPricePackage) {
    return null
  }

  const { price, value, duration: durationToDisplay } = lowestPricePackage
  const flightPrice = offer.flightPrices[airportCode]
  const result = getBundledWithFlightOfferDisplayPricing(offer.bundledWithFlightsOnly, flightPrice, price, value)

  if (result.isBundledFlightPriceDisplayable) {
    const { totalPrice, totalValue, flightPrice, shouldShowValue } = result
    return (
      <div>
        {offer.badge && <OfferBadgeTag badge={offer.badge} />}
        <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
          {pluralizeToString('night', durationToDisplay)}{flightPrice && ' + flights from'}
        </CaptionTextBlock>
        <Caption variant="medium">
          <BodyText variant="small" weight="bold" as="span">
            <FormatCurrency value={Math.ceil(totalPrice)} />
          </BodyText> total*
        </Caption>
        {shouldShowValue && (
          <Caption variant="medium">Valued up to <FormatCurrency value={Math.ceil(totalValue)} /></Caption>
        )}
      </div>
    )
  }

  return (
    <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
      Hotel + flight package available
    </CaptionTextBlock>
  )
}

function mapStateToProps(state: App.State) {
  return {
    airportCode: getDefaultAirportCode(state),
  }
}

export default connect(mapStateToProps)(BundledHotelPriceContent)
